import { apiClient } from './client'
import type { CursorPage } from './accounts'
import type { ConfigDefinitionView, ProfileSummary, UserView } from '../types/admin'

/**
 * `GET /users` — the tenant user roster for AdminSettingsScreen's "Users & Roles" tab.
 *
 * v64 merge: the on-the-wire `GET /api/v1/users` is now the capability-guarded, keyset-paginated
 * endpoint from user-admin Slice B (returns `CursorPage<UserView>`), the single source of truth for
 * the user roster. This helper unwraps `.items` so AdminSettingsScreen (which only renders a small
 * catalog view, not an operational list) keeps its simple `UserView[]` contract; a generous `limit`
 * covers a tenant's whole roster in one page (a demo tenant has dozens of users, not thousands).
 */
export async function listUsers(signal?: AbortSignal): Promise<UserView[]> {
  const { data } = await apiClient.get<CursorPage<UserView>>('/users?limit=200', { signal })
  return data.items
}

/** `GET /profiles` — additive (this story adds the HTTP shape over the already-published `ProfileApi.listVisible()`). */
export async function listProfiles(signal?: AbortSignal): Promise<ProfileSummary[]> {
  const { data } = await apiClient.get<ProfileSummary[]>('/profiles', { signal })
  return data
}

/** `GET /config-definitions` (already-published `ConfigDefinitionController`) — the platform config catalog. */
export async function listConfigDefinitions(signal?: AbortSignal): Promise<CursorPage<ConfigDefinitionView>> {
  const { data } = await apiClient.get<CursorPage<ConfigDefinitionView>>('/config-definitions', { signal })
  return data
}
